import {ApiClient} from '@hconnect/apiclient'
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions
} from '@tanstack/react-query'
import {AxiosError} from 'axios'

import {EventsSearchQueryKey} from '../../events/hooks'
import {Comment, CommentDto} from '../../events/types'
import {mapComment} from '../mappers'
import {CreateCommentParameter} from '../types'

export const CommentsQueryKey = 'events-comments'

type UseCommentsParams = {
  plantId: string
  eventId: string
  timezone: string
  options?: Omit<UseQueryOptions<Comment[], AxiosError>, 'queryKey'>
}

export const useComments = ({plantId, eventId, timezone, options}: UseCommentsParams) => {
  const path = `/shifthandover/${plantId}/events/${eventId}/comments`

  return useQuery<Comment[], AxiosError>({
    queryKey: [CommentsQueryKey, eventId, plantId, timezone],
    queryFn: async ({signal}) => {
      const response = await ApiClient.axiosInstance.get<CommentDto[]>(path, {signal})
      return response.data.map((comment) => mapComment(comment, timezone))
    },
    ...options
  })
}

type UseCreateCommentParams = {
  plantId: string
  timezone: string
  options?: UseMutationOptions<Comment, AxiosError, CreateCommentParameter>
}

export const useCreateComment = ({plantId, timezone, options}: UseCreateCommentParams) => {
  const queryClient = useQueryClient()

  return useMutation<Comment, AxiosError, CreateCommentParameter>({
    mutationFn: async ({eventId, description}) => {
      const path = `/shifthandover/${plantId}/events/${eventId}/comments`

      const response = await ApiClient.axiosInstance.post<CommentDto>(path, {description})
      return mapComment(response.data, timezone)
    },
    ...options,
    // make sure to invalidate the Queries after the regular onSuccess process is done
    onSuccess: async (data, variables, context, mutation) => {
      await queryClient.invalidateQueries({queryKey: [CommentsQueryKey]})
      await queryClient.invalidateQueries({queryKey: [EventsSearchQueryKey]})
      await options?.onSuccess?.(data, variables, context, mutation)
    }
  })
}
